/**
 * JOYWEB-2626 — decision core for "move a booking to another staff".
 *
 * Pure, dependency-free, structured-output only (no copy, no DOM). The caller
 * (calendar drag handler) gathers the facts from already-loaded calendar data
 * and converts them to the normalised shape below; the popover component turns
 * the returned warning objects into the conversational copy + icons.
 *
 * All times are minutes-since-midnight (integers) so no date library is needed
 * and the function is trivially unit-testable in node.
 *
 * @param {Object}   input
 * @param {Array}    input.services          moved group's services, each { servName:string, startMin:number, endMin:number }
 * @param {Object}   input.targetStaff       { userId:number, name:string, mappedServNames:string[]|null, workStartMin:number|null, workEndMin:number|null }
 * @param {Array}    input.existingBookings  bookings already on the target staff that day, EXCLUDING the moved group,
 *                                           each { startMin:number, endMin:number, custName?:string, servName?:string }
 * @returns {Array} warnings, in stable order (service → timing → availability). Each is one of:
 *   { type:'service',      servName }
 *   { type:'timing',       workStartMin, workEndMin, visitStartMin, visitEndMin }
 *   { type:'availability', startMin, endMin, custName, servName }   // one per clashing booking
 *
 * All warnings are advisory/override-able — this function never blocks a move.
 */
export function evaluateStaffMove(input) {
  const warnings = [];
  if (!input || !Array.isArray(input.services) || input.services.length === 0) return warnings;
  const services = input.services;
  const staff = input.targetStaff || {};
  const existing = Array.isArray(input.existingBookings) ? input.existingBookings : [];

  // 1) SERVICE — every service the target staff isn't set up for (matched by NAME, since
  // calendar events carry the service name, not its id). Only checked when the mapped-name
  // list is supplied; absent (null) => unknown => skipped (no false warning).
  // Both sides come from services_mst.serv_name (calendar via live JOIN, mapping via direct read),
  // so they match today — but trim-normalise defensively so a stray edge space can never surface a
  // false "doesn't offer". Compare on the normalised value; keep the original name for display.
  const mapped = Array.isArray(staff.mappedServNames) ? staff.mappedServNames.map(normName) : null;
  if (mapped !== null) {
    services.forEach(sv => {
      if (mapped.indexOf(normName(sv.servName)) === -1) warnings.push({ type: 'service', servName: sv.servName });
    });
  }

  // Visit span = earliest start .. latest end across the moved group.
  const visitStartMin = Math.min.apply(null, services.map(s => s.startMin));
  const visitEndMin = Math.max.apply(null, services.map(s => s.endMin));

  // 2) TIMING — visit falls outside the target staff's scheduled window.
  // Only checked when both bounds are known (null == unknown hours -> skip, don't false-warn).
  if (isNum(staff.workStartMin) && isNum(staff.workEndMin) &&
      (visitStartMin < staff.workStartMin || visitEndMin > staff.workEndMin)) {
    warnings.push({
      type: 'timing',
      workStartMin: staff.workStartMin, workEndMin: staff.workEndMin,
      visitStartMin: visitStartMin, visitEndMin: visitEndMin,
    });
  }

  // 3) AVAILABILITY — clashes with existing bookings on the target staff (one warning per clash).
  existing.forEach(b => {
    if (isNum(b.startMin) && isNum(b.endMin) && overlaps(visitStartMin, visitEndMin, b.startMin, b.endMin)) {
      warnings.push({
        type: 'availability',
        startMin: b.startMin, endMin: b.endMin,
        custName: b.custName || '', servName: b.servName || '',
      });
    }
  });

  return warnings;
}

/** Half-open overlap: [aStart,aEnd) intersects [bStart,bEnd). Touching edges do NOT clash. */
export function overlaps(aStart, aEnd, bStart, bEnd) {
  return aStart < bEnd && bStart < aEnd;
}

function isNum(v) { return typeof v === 'number' && !isNaN(v); }

/** Normalise a service name for comparison: trim surrounding whitespace; pass non-strings through. */
function normName(v) { return typeof v === 'string' ? v.trim() : v; }
